import { z } from 'zod';
import { DEFAULT_INBOX_SORT, INBOX_SORT_VALUES } from '../../../utils/inbox-filters';

/**
 * The sort orders the inbox offers, shared with the page's Sort control so the two
 * cannot drift. `.catch()` keeps an unrecognised value (a stale bookmark, a
 * hand-edited URL) falling back to the default rather than 400ing the whole list.
 */
export const InboxSortSchema = z.enum(INBOX_SORT_VALUES).catch(DEFAULT_INBOX_SORT);

export type InboxSort = z.infer<typeof InboxSortSchema>;

// Query schema for inbox
export const InboxQuerySchema = z.object({
  sortBy: InboxSortSchema.default(DEFAULT_INBOX_SORT),
  categoryGroupId: z.string().optional(),
  categoryId: z.string().optional(),
  status: z.string().optional(),
  dataSourceId: z.string().optional(),
  /**
   * A source that is not a DATA_SOURCES row.
   *
   * Vendoo listings are dataSourceId 1 — the same id as anything a seller posts on
   * SellWild — and are only distinguishable by SUPPLY_LISTINGS.vendooListingId. So
   * "show me Vendoo" cannot be expressed as a dataSourceId and needs its own param.
   */
  source: z.enum(['vendoo']).optional(),
  search: z.string().optional(),
  userId: z.string().optional(),
  featured: z.string().optional(),
  partnerFeatured: z.string().optional(),
  sold: z.string().optional(),
  shippable: z.string().optional(),
  /** 'true' keeps deal_score >= DEALS_ONLY_MIN_SCORE / quality_pct >= QUALITY_ONLY_MIN_PCT (utils/deal-quality-filter.ts). */
  deals: z.string().optional(),
  quality: z.string().optional(),
  /** 'true' keeps only listings hidden from rows and browse (utils/not-recommended.ts), for review. */
  nsfw: z.string().optional(),
  showArchived: z.string().optional(),
  /**
   * 'true' shows every listing of every seller. By default the inbox hides a
   * seller's listings past the per-seller limit (SUPPLY_LISTINGS.sellerRank
   * against the `maxPerSellerPerPage` ranking knob), so the top of the list is
   * a mix of sellers rather than one seller's dump.
   */
  allPerSeller: z.string().optional(),
  /**
   * Listings per seller for this view: a positive number, or 'all'. Absent
   * follows the ranking knob. Takes precedence over allPerSeller.
   */
  perSeller: z.string().optional(),
  page: z.string().optional(),
  limit: z.string().optional(),
});

export type InboxQuery = z.infer<typeof InboxQuerySchema>;

// Response schema for inbox
export const InboxResponseSchema = z.discriminatedUnion('success', [
  z.object({
    success: z.literal(true),
    listings: z.array(z.any()),
    count: z.number(),
    total: z.number(),
    page: z.number(),
    limit: z.number(),
    /** The per-seller limit applied to this page; null when none was. */
    perSellerLimit: z.number().nullable().optional(),
    /**
     * Newest first only: the last two weeks by Pacific day, each with its
     * total and the page it starts on (utils/inbox-days.ts).
     */
    days: z.array(z.object({ date: z.string(), label: z.string(), total: z.number(), firstPage: z.number() })).optional(),
  }),
  z.object({
    success: z.literal(false),
    error: z.string(),
    details: z.string().optional(),
  }),
]);

export type InboxResponse = z.infer<typeof InboxResponseSchema>;

// Alias for backwards compatibility with test fixtures
export const GetListingsInboxResponseSchema = InboxResponseSchema;
export type GetListingsInboxResponse = InboxResponse;
